import { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { applyActionCode, sendEmailVerification, getAuth } from 'firebase/auth';
import { auth } from '../firebase';

export default function VerifyEmail() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [status, setStatus] = useState<'loading' | 'success' | 'error' | 'expired' | 'already-verified'>('loading');
  const [errorMessage, setErrorMessage] = useState('');
  const [resending, setResending] = useState(false);
  const [cooldownSeconds, setCooldownSeconds] = useState(0);

  const mode = searchParams.get('mode');
  const oobCode = searchParams.get('oobCode');

  // Cooldown timer
  useEffect(() => {
    if (cooldownSeconds > 0) {
      const timer = setTimeout(() => setCooldownSeconds(cooldownSeconds - 1), 1000);
      return () => clearTimeout(timer);
    }
  }, [cooldownSeconds]);

  useEffect(() => {
    const verifyEmail = async () => {
      // Check if user is already verified
      const user = auth.currentUser;
      if (user) {
        await user.reload();
        if (user.emailVerified) {
          setStatus('already-verified');
          return;
        }
      }

      // Check if we have the required parameters
      if (mode !== 'verifyEmail' || !oobCode) {
        setStatus('error');
        setErrorMessage('Invalid verification link. Please request a new verification email.');
        return;
      }

      try {
        // Apply the verification code
        await applyActionCode(auth, oobCode);

        // Reload user to get updated emailVerified status
        if (auth.currentUser) {
          await auth.currentUser.reload();
          // Force token refresh to update backend claims
          await auth.currentUser.getIdToken(true);
        }

        setStatus('success');

        // Redirect to AI Builder after 3 seconds
        setTimeout(() => {
          navigate('/ai-builder');
        }, 3000);
      } catch (error: any) {
        console.error('Email verification error:', error);

        if (error.code === 'auth/expired-action-code') {
          setStatus('expired');
          setErrorMessage('This verification link has expired.');
        } else if (error.code === 'auth/invalid-action-code') {
          setStatus('expired');
          setErrorMessage('This verification link has already been used or is invalid.');
        } else {
          setStatus('error');
          setErrorMessage(error.message || 'An error occurred during verification.');
        }
      }
    };

    verifyEmail();
  }, [mode, oobCode, navigate]);

  const handleResendEmail = async () => {
    if (cooldownSeconds > 0) return;

    setResending(true);

    try {
      const user = auth.currentUser;
      if (!user) {
        setErrorMessage('You must be logged in to resend the verification email. Please log in and try again.');
        setResending(false);
        return;
      }

      // Add ActionCodeSettings for custom continue URL
      const actionCodeSettings = {
        url: `${window.location.origin}/ai-builder`,
        handleCodeInApp: false,
      };

      await sendEmailVerification(user, actionCodeSettings);
      setErrorMessage('');
      setStatus('success');
      setCooldownSeconds(60);

      // Show success message
      setTimeout(() => {
        setStatus('expired');
        setErrorMessage('New verification email sent! Check your inbox and use the newest link.');
      }, 500);
    } catch (err: any) {
      if (err.code === 'auth/too-many-requests') {
        setErrorMessage('Too many requests. Please wait a few minutes before trying again.');
      } else {
        setErrorMessage('Failed to send verification email. Please try again or log in first.');
      }
      console.error('Error sending verification email:', err);
    } finally {
      setResending(false);
    }
  };

  const handleGoToLogin = () => {
    navigate('/login');
  };

  const handleGoToAIBuilder = () => {
    navigate('/ai-builder');
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 to-sky-100 flex items-center justify-center p-4">
      <div className="max-w-md w-full bg-white rounded-2xl shadow-2xl p-8">
        {/* Loading State */}
        {status === 'loading' && (
          <div className="text-center">
            <div className="inline-block animate-spin rounded-full h-16 w-16 border-b-4 border-blue-600 mb-6"></div>
            <h2 className="text-2xl font-bold text-gray-900 mb-2">Verifying Your Email...</h2>
            <p className="text-gray-600">Please wait while we confirm your email address.</p>
          </div>
        )}

        {/* Success State */}
        {status === 'success' && (
          <div className="text-center">
            <div className="w-20 h-20 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-6">
              <svg className="w-12 h-12 text-green-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
            </div>
            <h2 className="text-3xl font-bold text-gray-900 mb-3">Email Verified!</h2>
            <p className="text-gray-600 mb-6">
              Your email has been successfully verified. You can now access all AI-powered features.
            </p>
            <p className="text-sm text-gray-500 mb-6">
              Redirecting to AI Builder in 3 seconds...
            </p>
            <button
              onClick={handleGoToAIBuilder}
              className="w-full inline-flex justify-center items-center px-6 py-3 border border-transparent text-base font-medium rounded-lg text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 shadow-sm transition-colors"
            >
              Go to AI Builder Now
            </button>
          </div>
        )}

        {/* Already Verified State */}
        {status === 'already-verified' && (
          <div className="text-center">
            <div className="w-20 h-20 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-6">
              <svg className="w-12 h-12 text-blue-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
            </div>
            <h2 className="text-3xl font-bold text-gray-900 mb-3">Already Verified</h2>
            <p className="text-gray-600 mb-6">
              Your email is already verified. You're all set!
            </p>
            <button
              onClick={handleGoToAIBuilder}
              className="w-full inline-flex justify-center items-center px-6 py-3 border border-transparent text-base font-medium rounded-lg text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 shadow-sm transition-colors"
            >
              Go to AI Builder
            </button>
          </div>
        )}

        {/* Expired/Invalid Link State */}
        {status === 'expired' && (
          <div className="text-center">
            <div className="w-20 h-20 bg-yellow-100 rounded-full flex items-center justify-center mx-auto mb-6">
              <svg className="w-12 h-12 text-yellow-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
            </div>
            <h2 className="text-2xl font-bold text-gray-900 mb-3">Link Expired or Already Used</h2>
            <p className="text-gray-600 mb-4">
              {errorMessage || 'This verification link is no longer valid.'}
            </p>

            <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-6 text-left">
              <p className="text-sm text-blue-900 font-medium mb-2">💡 Common Reasons:</p>
              <ul className="text-xs text-blue-800 space-y-1 list-disc list-inside">
                <li>School email systems scan links automatically</li>
                <li>Link was already clicked before</li>
                <li>Link is older than the newest verification email</li>
              </ul>
            </div>

            <div className="space-y-3">
              <button
                onClick={handleResendEmail}
                disabled={resending || cooldownSeconds > 0}
                className="w-full inline-flex justify-center items-center px-6 py-3 border border-transparent text-base font-medium rounded-lg text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed shadow-sm transition-colors"
              >
                {resending ? (
                  <>
                    <svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                    </svg>
                    Sending...
                  </>
                ) : cooldownSeconds > 0 ? (
                  `Wait ${cooldownSeconds}s`
                ) : (
                  <>
                    <svg className="w-5 h-5 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                    </svg>
                    Send New Verification Email
                  </>
                )}
              </button>

              <button
                onClick={handleGoToLogin}
                className="w-full inline-flex justify-center items-center px-6 py-3 border border-gray-300 text-base font-medium rounded-lg text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 shadow-sm transition-colors"
              >
                Go to Login
              </button>
            </div>
          </div>
        )}

        {/* Error State */}
        {status === 'error' && (
          <div className="text-center">
            <div className="w-20 h-20 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-6">
              <svg className="w-12 h-12 text-red-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </div>
            <h2 className="text-2xl font-bold text-gray-900 mb-3">Verification Failed</h2>
            <p className="text-gray-600 mb-6">
              {errorMessage || 'We couldn\'t verify your email. Please try again.'}
            </p>

            <div className="space-y-3">
              <button
                onClick={handleResendEmail}
                disabled={resending || cooldownSeconds > 0}
                className="w-full inline-flex justify-center items-center px-6 py-3 border border-transparent text-base font-medium rounded-lg text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed shadow-sm transition-colors"
              >
                {resending ? 'Sending...' : cooldownSeconds > 0 ? `Wait ${cooldownSeconds}s` : 'Send New Verification Email'}
              </button>

              <button
                onClick={handleGoToLogin}
                className="w-full inline-flex justify-center items-center px-6 py-3 border border-gray-300 text-base font-medium rounded-lg text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 shadow-sm transition-colors"
              >
                Go to Login
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
